import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Constants
const NODE_RADIUS = 40;
const ROOT_TO_FIRST_LEVEL_SPACING = 200; // Custom spacing from root to level 1
const FIRST_LEVEL_TO_OTHER_SPACING = 150;

const LOSBranchView = ({ data, config = {
  ROOT_NODE_COLOR: '#9e703c',
  PARENT_NODE_COLOR: '#367378',
  CHILD_NODE_COLOR: '#b7503f',
  LINK_COLOR: "#91b6bb",
  TEXT_COLOR: "#ffffff",
  FONT_SIZE: '12px',
} }) => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const {t} = useTranslation();

  const [error, setError] = useState(null); // State for error handling

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Validate data structure
  useEffect(() => {
    const isValid = validateDataStructure(data);
    if (!isValid) {
      setError(t("Invalid data structure"));
    } else {
      setError(null);
    }
  }, [data]);

  if (error) {
    return (
      <div style={{ color: 'red', textAlign: 'center', marginTop: '20px' }}>
        <h3>{t("Error")}</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <ZoomableSVG width={dimensions.width} height={dimensions.height}>
      <TreeViewComponent width={dimensions.width} height={dimensions.height} data={data} config={config} />
    </ZoomableSVG>
  );
};

// Helper function to validate the data structure
function validateDataStructure(node) {
  if (!node || typeof node !== 'object') return false;
  if (!node.id || !node.name) return false;
  if (!node.connections || (node.connections && !Array.isArray(node.connections))) return false;
  if (node.connections) {
    return node.connections.every(validateDataStructure);
  }
  return true;
}

// Helper Functions
function transformData(node) {
  if (!node) return null;
  return {
    id: node.id,
    name: node.name,
    children: (node.connections || []).map(transformData),
  };
}

function getCenterChild(children) {
  if (!children || children.length === 0) return null;
  return children[Math.floor((children.length - 1) / 2)];
}

// Initialization Function
function initialize(svg, width, height, data, nodeStates, toggleChildren, config) {
  const transformedData = transformData(data);

  // Tree layout with separation
  const tree = d3.tree()
    .size([width, height]) // Define the full dimensions of the tree
    .separation((a, b) => {
      // Prevent overlaps by dynamically increasing spacing based on depth
      return a.parent === b.parent ? (1.5 + a.depth * 0.5) : 2;
    });

  // All nodes
  const nodes = tree(d3.hierarchy(transformedData)
    .sort((a, b) => d3.ascending(a.data.name, b.data.name)));

  // Calculate equal spacing for levels > 1
  const maxDepth = d3.max(nodes.descendants(), d => d.depth);
  const remainingRadius = ROOT_TO_FIRST_LEVEL_SPACING + FIRST_LEVEL_TO_OTHER_SPACING; // Remaining radius for levels > 1
  const equalLevelSpacing = remainingRadius / (maxDepth - 1); // Equal spacing for levels > 1
  // Apply spacing to all nodes and Custom radius for nodes based on some property
  nodes.descendants().forEach(d => {
    d.r = NODE_RADIUS;
    if (d.depth > 0) {
      d.y = ROOT_TO_FIRST_LEVEL_SPACING + equalLevelSpacing * (d.depth - 1);
    }
  });

  // Apply node visibility based on the `nodeStates`
  nodes.each((d) => {
    if (nodeStates[d.data.id] === false) {
      d.children = []; // Hide children if the state is false
    }
  });

  // Links (edges between nodes)
  svg
    .selectAll(".link")
    .data(nodes.links())
    .join("path")
    .attr("class", "link")
    .attr("d", d3.linkHorizontal().x((d) => d.x).y((d) => d.y))
    .attr("fill", "none")
    .attr("stroke-width", 2)
    .attr("stroke", config.LINK_COLOR);


  // Nodes
  const nodeGroups = svg
    .selectAll(".node")
    .data(nodes.descendants())
    .join("g")
    .attr("transform", (d) => `translate(${d.x},${d.y})`);

  nodeGroups
    .append("circle")
    .attr("r", NODE_RADIUS)
    .attr("fill", (d) => {
      if (!d.parent) {
        // Root node
        return config.ROOT_NODE_COLOR;
      } else if (d.children && nodeStates[d.data.id] !== false) {
        // Parent node
        return config.PARENT_NODE_COLOR;
      } else {
        // Leaf (child) node
        return config.CHILD_NODE_COLOR;
      }
    }).on("click", (event, d) => {
      // if (d.children) {
      //   // Toggle children visibility on click
      //   toggleChildren(d.data.id);
      // }
    });

  nodeGroups
    .append("text")
    .attr("text-anchor", "middle")
    .attr("fill", config.TEXT_COLOR)
    .attr("font-size", config.FONT_SIZE)
    .selectAll("tspan")
    .data((d) => {
      const words = d.data.name.split(/\s+/); // Split text into words
      const maxCharsPerLine = 10; // Max characters per line
      const lines = [];
      let line = [];
      words.forEach((word) => {
        const testLine = [...line, word].join(" ");
        if (testLine.length > maxCharsPerLine) {
          lines.push(line.join(" "));
          line = [word];
        } else {
          line.push(word);
        }
      });
      if (line.length) lines.push(line.join(" "));
      return lines;
    })
    .join("tspan")
    .attr("x", 0)
    .attr("y", (d, i, nodes) => {
      const totalLines = nodes.length; // Total number of lines
      const offset = -((totalLines - 1) / 2); // Center text vertically
      return `${offset + i * 1.4}em`; // Increase line spacing for readability
    })
    .text((d) => d) // Set text for each line
    .on("click", (event, d) => {
      if (d.children) {
        // Toggle children visibility on click (on text)
        toggleChildren(d.data.id);
      }
      event.stopPropagation(); // Prevent the click from bubbling to the circle
    });

  // Apply toggling behavior for children visibility
  nodeGroups
    .each((d) => {
      if (nodeStates[d.data.id] === false) {
        // Hide children if the state is false
        d.children = [];
      } else {
        // Show children if the state is true
        d.children = d.data.children || [];
      }
    });
}

// TreeViewComponent
const TreeViewComponent = ({ width, height, data, config }) => {
  const [nodeStates, setNodeStates] = useState({}); // To track the state of each node (whether its children are visible) 
  const chartGroupRef = useRef();

  // Toggle the visibility of the children of a node
  const toggleChildren = (nodeId) => {
    setNodeStates((prevState) => ({
      ...prevState,
      [nodeId]: prevState[nodeId] === false ? true : false, // Toggle the visibility
    }));
  };

  useEffect(() => {
    const svg = d3.select(chartGroupRef.current);
    svg.selectAll("*").remove(); // Clear previous visualization
    initialize(svg, width, height, data, nodeStates, toggleChildren, config);
  }, [width, height, data, nodeStates]);

  return <g className="zoom-layer" ref={chartGroupRef}></g>; // Only group element, no svg
};

const ZoomableSVG = ({ children, width, height }) => {
  const svgRef = useRef();

  useEffect(() => {
    const defaultScale = 1; // Set your default scale here
    const defaultWidth = 0;
    const defaultHeight = 0;

    const zoom = d3.zoom().on("zoom", (event) => {
      const { x, y, k } = event.transform;
      d3.select(svgRef.current)
        .select("g.zoom-layer")
        .attr("transform", `translate(${x},${y})scale(${k})`);
    });

    // Initialize zoom with default scale (applied once on load)
    d3.select(svgRef.current)
      .call(zoom)
      .call(zoom.transform, d3.zoomIdentity.translate(defaultWidth, defaultHeight + 50).scale(defaultScale));

    return () => d3.select(svgRef.current).on(".zoom", null); // Cleanup zoom
  }, [width, height]);

  return (
    <svg ref={svgRef} id="visualization-layer" width={width} height={height}>
      {children}
    </svg>
  );
};

LOSBranchView.propTypes = {
  data: PropTypes.object.isRequired,
};

export default LOSBranchView;
